import { TruncationMode } from '../../../../typography/text-ellipsis/TextEllipsis.js';
import { ELLIPSIS_CHARACTER as _ELLIPSIS_CHARACTER } from '../../../../typography/utils.js';

/**
 * Approximate pixel height for a single line of text.
 * Used by consumers for layout calculations; not involved in truncation logic.
 * @public
 */
export const TEXT_HEIGHT = 12;

/**
 * Computes the pixel width of a string using a per-character width map.
 * Unknown characters fall back to the width of '\@'.
 *
 * @param text - The string to measure.
 * @param fontWidthMap - Map of character to pixel width for the active font.
 * @returns The total width in CSS pixels.
 * @public
 */
export const calculateWidth = (
  text: string,
  fontWidthMap: Record<string, number>,
): number => {
  let totalWidth = 0;
  for (const char of text) {
    totalWidth += fontWidthMap[char] ?? fontWidthMap['@'];
  }
  return totalWidth;
};

/**
 * Iteratively accumulates characters produced by a generator until the available
 * capacity (availableWidth minus ellipsis width) is reached, then returns the
 * accumulated characters. This function does not apply the ellipsis; it only
 * determines what characters can be kept.
 *
 * @param availableWidth - Maximum width allowed for the final text including the ellipsis.
 * @param fontWidthMap - Map of character to pixel width for the active font.
 * @param generator - Generator that yields characters in the order they should be kept.
 * @returns The sequence of kept characters that fit within the capacity.
 * @internal
 */
function accumulateUntilCapacity(
  availableWidth: number,
  generator: Generator<string>,
  measureText: (text: string) => number,
) {
  let keptText = '';
  let keptWidth = 0;
  const capacity = availableWidth - measureText(_ELLIPSIS_CHARACTER);

  for (const ch of generator) {
    const chWidth = measureText(ch);
    if (keptWidth + chWidth > capacity) {
      return keptText;
    }
    keptText += ch;
    keptWidth += chWidth;
  }

  return keptText;
}

/**
 * Tolerance in CSS pixels for width comparisons to avoid subpixel off-by-ones
 * that cause visual clipping at the right edge.
 * @internal
 */
const EPSILON_PX = 0.5;

/**
 * If the available width is below this threshold (considering epsilon), the function
 * returns only the ellipsis to avoid unstable truncation artifacts.
 * @internal
 */
const MIN_AVAILABLE_WIDTH = 19;

/**
 * Controls which side of the string the middle-truncation generator starts
 * collecting characters from. 'start' keeps head characters first (e.g.
 * "3…" for "37%"), 'end' keeps tail characters first (e.g. "…%" for "37%").
 * Only affects the 'middle' truncation mode.
 * @public
 */
export type TruncationPriority = 'start' | 'end';

/**
 * Produces the final truncated string by inserting an ellipsis in the appropriate
 * position for each truncation mode, based on the sequence of kept characters.
 *
 * - 'start': Places ellipsis at the beginning and reverses kept characters.
 * - 'end': Places ellipsis at the end.
 * - 'middle': Interleaves characters into head and tail based on index parity,
 *   then places the ellipsis between head and tail.
 * @internal
 */
const buildTruncatedOutput = {
  start: (kept: string) => {
    let reversed = '';
    for (const ch of kept) {
      reversed = ch + reversed;
    }
    return `${_ELLIPSIS_CHARACTER}${reversed}`;
  },
  end: (kept: string) => `${kept}${_ELLIPSIS_CHARACTER}`,
  middle: (kept: string, priority: TruncationPriority) => {
    let head = '';
    let tail = '';
    if (priority === 'start') {
      for (let i = 0; i < kept.length; i++) {
        if (i % 2 === 0) {
          head += kept[i];
        } else {
          tail = kept[i] + tail;
        }
      }
    } else {
      for (let i = 0; i < kept.length; i++) {
        if (i % 2 === 0) {
          tail = kept[i] + tail;
        } else {
          head += kept[i];
        }
      }
    }
    return `${head}${_ELLIPSIS_CHARACTER}${tail}`;
  },
};

/**
 * Character generators that yield the sequence of characters to keep for each
 * truncation mode before the ellipsis is applied.
 *
 * - 'start': Yields from end to start (tail-first).
 * - 'end': Yields from start to end (head-first).
 * - 'middle': Alternates from both ends toward the center.
 *   The `priority` parameter controls which end yields first.
 * @internal
 */
const keepSequenceGenerators = {
  start: function* fromTail(text: string) {
    for (let i = text.length - 1; i >= 0; i--) {
      yield text[i];
    }
  },
  end: function* fromHead(text: string) {
    for (const ch of text) {
      yield ch;
    }
  },
  middle: function* fromBothEnds(text: string, priority: TruncationPriority) {
    let left = 0;
    let right = text.length - 1;
    while (left < right) {
      if (priority === 'start') {
        yield text[left++];
        yield text[right--];
      } else {
        yield text[right--];
        yield text[left++];
      }
    }
    if (left === right) {
      yield text[left];
    }
  },
};

/**
 * Truncates a string to fit within the given width by placing an ellipsis at the
 * start, middle, or end, depending on the truncation mode. If the text fits, it
 * is returned unchanged. If the width is too small, returns only the ellipsis.
 *
 * Key behaviors:
 * - Uses a generator to determine which characters to keep for each mode.
 * - Accumulates characters until the capacity (availableWidth minus ellipsis width).
 * - Applies EPSILON tolerance to treat near-equal widths as fitting.
 * - Uses MIN_AVAILABLE_WIDTH to return a stable ellipsis when space is minimal.
 *
 * @param text - The input string to truncate.
 * @param availableWidth - Max allowed width in CSS pixels for the final output.
 * @param fontWidthMap - Per-character width map for the active font.
 * @param truncationMode - Where to place the ellipsis: 'start' | 'middle' | 'end'.
 * @param priority - For 'middle' mode only: 'start' keeps head characters first
 *   (e.g. "3…"), 'end' keeps tail characters first (e.g. "…%").
 *   Defaults to 'end' to match the legacy behavior.
 * @returns The truncated string with ellipsis applied according to the mode.
 * @public
 */
export const truncateString = (
  text: string,
  availableWidth: number,
  fontWidthMap: Record<string, number>,
  truncationMode: TruncationMode = 'middle',
  priority: TruncationPriority = 'end',
  measureText?: (text: string) => number,
) => {
  const measure =
    measureText ?? ((text: string) => calculateWidth(text, fontWidthMap));

  // The EPSILON_PX tolerance allows text that is very close to the available
  // width to be considered as fitting, avoiding unnecessary truncation for
  // borderline cases where subpixel differences don't cause visible overflow.
  const fullTextWidth = measure(text);
  const roundedAvailableWidth = Math.round(availableWidth) + EPSILON_PX;

  if (fullTextWidth <= roundedAvailableWidth) {
    return text;
  }

  if (MIN_AVAILABLE_WIDTH >= roundedAvailableWidth) {
    return _ELLIPSIS_CHARACTER;
  }

  const keptChars = accumulateUntilCapacity(
    availableWidth,
    truncationMode === 'middle'
      ? keepSequenceGenerators.middle(text, priority)
      : keepSequenceGenerators[truncationMode](text),
    measure,
  );

  return truncationMode === 'middle'
    ? buildTruncatedOutput.middle(keptChars, priority)
    : buildTruncatedOutput[truncationMode](keptChars);
};
